// TwoDAvatar.jsx — Sprint 4A Fix 5: Benjamin & John lip sync fixed
// Sprint 4B: Lip color band-aid for Benjamin/John (pink lips look feminine)
// ROOT CAUSE: isTalkingRef guard was blocking re-entry on avatar switches.
//             Male CSS filter was covering mouth overlay on some browsers.
//             startTalking() was not firing reliably before Web Speech onstart.
// FIXES:
//   1. Reset isTalkingRef on every new speak() call (cancel old before guard check)
//   2. Remove CSS filter from mouth overlay div (keep it only on avatar img)
//   3. Kick startTalking() immediately AND on onstart (belt + suspenders)
//   4. avatarName passed into speak closure via ref — no stale closure issues
//   5. STOPGAP: SVG color-matrix filter desaturates pink/red lip tones on
//      male avatars. This is a band-aid only — replace source PNGs with
//      BizBarrett's original mood card images when supplied (see his request
//      "make him look like original mood card, no extra artwork").

import React, { useState, useEffect, useRef } from "react";
import { speakWithAvatarVoice, stopAvatarVoice } from "../utils/avatarVoice";

const AVATAR_CONFIG = {
  Camille:  { imgWidth:"480px", imgHeight:"480px", imgTop:0,  imgLeft:0,  mouthTop:62, mouthLeft:50, mouthSize:52, offsetX:-2, offsetY:-40 },
  Prithi:   { imgWidth:"480px", imgHeight:"480px", imgTop:0,  imgLeft:0,  mouthTop:63, mouthLeft:49.9, mouthSize:62, offsetX:0,  offsetY:-45 },
  Benjamin: { imgWidth:"600px", imgHeight:"400px", imgTop:0,  imgLeft:0,  mouthTop:60, mouthLeft:50, mouthSize:60, offsetX:0,  offsetY:-78 },
  John:     { imgWidth:"780px", imgHeight:"380px", imgTop:0,  imgLeft:0,  mouthTop:61.5, mouthLeft:49.9, mouthSize:48, offsetX:-3, offsetY:-86 },
  Margie:   { imgWidth:"480px", imgHeight:"480px", imgTop:0,  imgLeft:0,  mouth: { x:765.73, y:449.94, w:143.63, natW:1532, natH:1026 } },
  Genie:    { imgWidth:"480px", imgHeight:"480px", imgTop:0,  imgLeft:0,  mouthTop:62, mouthLeft:50, mouthSize:52, offsetX:-2, offsetY:-40 },
};


const AVATAR_IMAGES = {
  Camille:  { neutral:"/2d/camille_neutral.png",  happy:"/2d/camille_happy.png",  sad:"/2d/camille_sad.png",  angry:"/2d/camille_angry.png",  love:"/2d/camille_love.png",  suggestive:"/2d/camille_suggestive.png"  },
  Prithi:   { neutral:"/2d/prithi_neutral.png",   happy:"/2d/prithi_happy.png",   sad:"/2d/prithi_sad.png",   angry:"/2d/prithi_angry.png",   love:"/2d/prithi_love.png",   suggestive:"/2d/prithi_suggestive.png"   },
  Benjamin: { neutral:"/2d/benji_neutral.png",    happy:"/2d/benji_happy.png",    sad:"/2d/benji_sad.png",    angry:"/2d/benji_angry.png",    love:"/2d/benji_love.png",    suggestive:"/2d/benji_suggestive.png"    },
  John:     { neutral:"/2d/john_neutral.png",     happy:"/2d/john_happy.png",     sad:"/2d/john_sad.png",     angry:"/2d/john_angry.png",     love:"/2d/john_love.png",     suggestive:"/2d/john_suggestive.png"     },
  Margie:   { neutral:"/2d/margie_neutral.png",   happy:"/2d/margie_happy.png",   sad:"/2d/margie_sad.png",   angry:"/2d/margie_angry.png",   love:"/2d/margie_love.png",   suggestive:"/2d/margie_suggestive.png"   },
  // Genie "happy" (client QA, 10 Oct): genie_happy.png has half-closed eyes and
  // read as bored; happy is the open, bright-eyed face with a smile at rest
  // (REST_MOUTH below).
  Genie:    { neutral:"/2d/genie_neutral.png",    happy:"/2d/genie_neutral.png",    sad:"/2d/genie_sad.png",    angry:"/2d/genie_angry.png",    love:"/2d/genie_love.png",    suggestive:"/2d/genie_suggestive.png"    },
};

// The mouth shown while not talking, per avatar and emotion (default: closed).
// mouth_smile.png is mouth_slightly_open.png with the corners curved up.
const REST_MOUTH = {
  Genie: { happy: "/mouths/mouth_smile.png" },
};

const MOUTHS = [
  "/mouths/mouth_closed.png",
  "/mouths/mouth_slightly_open.png",
  "/mouths/mouth_open_small.png",
  "/mouths/mouth_open_medium.png",
  "/mouths/mouth_open_wide.png",
  "/mouths/mouth_round.png",
];

// LAZY_2D_FACES_ENABLED (default on, 28 Oct 2026): preload only the chosen
// 2D host's faces and the mouths, when the 2D host is shown (was every face of
// every host at page load, ~28 MB, even in 3D mode).
const _cache = {};
const preload = (srcs) => srcs.forEach(src => { if (!_cache[src]) { const i = new Image(); i.src = src; _cache[src] = i; } });
const allFaces = () => [...Object.values(AVATAR_IMAGES).flatMap(m => Object.values(m)), ...MOUTHS, ...Object.values(REST_MOUTH).flatMap(m => Object.values(m))];
if (process.env.REACT_APP_LAZY_2D_FACES_ENABLED === "false") preload(allFaces());
function preloadHost(avatarName) {
  const map = AVATAR_IMAGES[avatarName] || AVATAR_IMAGES.Camille;
  preload([...Object.values(map), ...MOUTHS, ...Object.values(REST_MOUTH[avatarName] || {})]);
}

const TALK_SEQ      = [0, 2, 4, 2, 3, 1, 4, 2, 5, 1, 3, 0];
const TALK_INTERVAL = 110;

function getVoice(avatarName) {
  const voices = window.speechSynthesis?.getVoices() || [];
  const isMale = avatarName === "Benjamin" || avatarName === "John";
  if (isMale) {
    return (
      voices.find(v => v.name === "Microsoft David - English (United States)") ||
      voices.find(v => v.name === "Google UK English Male") ||
      voices.find(v => v.name.toLowerCase().includes("david")) ||
      voices.find(v => v.name.toLowerCase().includes("james")) ||
      voices.find(v => v.name.toLowerCase().includes("daniel")) ||
      voices.find(v => v.name.toLowerCase().includes("guy")) ||
      voices.find(v => v.lang?.startsWith("en") && v.name.toLowerCase().includes("male")) ||
      voices.find(v => v.lang?.startsWith("en")) ||
      null
    );
  } else {
    return (
      voices.find(v => v.name === "Microsoft Zira - English (United States)") ||
      voices.find(v => v.name === "Google US English") ||
      voices.find(v => v.name === "Samantha") ||
      voices.find(v => v.name.toLowerCase().includes("zira")) ||
      voices.find(v => v.name.toLowerCase().includes("samantha")) ||
      voices.find(v => v.name.toLowerCase().includes("victoria")) ||
      voices.find(v => v.name.toLowerCase().includes("karen")) ||
      voices.find(v => v.name.toLowerCase().includes("female")) ||
      voices.find(v => v.lang?.startsWith("en") && !v.name.toLowerCase().includes("male")) ||
      voices.find(v => v.lang?.startsWith("en")) ||
      null
    );
  }
}

// ── STOPGAP: SVG color-matrix filters to desaturate pink/red lip tones ─────
// This is a band-aid only. Each matrix pulls back the red/pink channel
// relationship so male avatar lips don't read as lipstick-pink, tuned per
// avatar to match their existing skin-tone filter on the base image.
// It does NOT perfectly match BizBarrett's original mood card art — replace
// source PNGs with his supplied images when available, then these filters
// can be removed entirely.
//
// BENJAMIN — warm deep brown lip tone (matches sepia(0.3) hue-rotate(5deg)
// skin filter). Pulls red channel down harder and shifts toward brown/umber.
const LIP_MATRIX_BENJAMIN = `
  0.65 0.18 0.10 0 0.02
  0.10 0.78 0.08 0 0.01
  0.08 0.14 0.72 0 0.01
  0    0    0    1 0
`;
const LIP_SATURATE_BENJAMIN = "0.5";

// JOHN — cooler muted tan lip tone (matches lighter sepia(0.1) brightness
// skin filter). Lighter pull-back, less brown shift, more neutral desaturation.
const LIP_MATRIX_JOHN = `
  0.78 0.13 0.07 0 0.01
  0.06 0.90 0.06 0 0
  0.05 0.10 0.85 0 0
  0    0    0    1 0
`;
const LIP_SATURATE_JOHN = "0.65";


// Genie: lips in Genie's body colour (the skin blue on genie_*.png, about RGB
// 0,125,222 around the mouth). Each pixel moves by how much redder than green
// it is, so the pink lips (average 204,91,107) land on that blue with their
// shading kept, while white teeth and the dark inside of the mouth (red about
// equal to green) stay as they are.
const LIP_MATRIX_GENIE = `
  -0.805 1.805 0     0 0
   0.301 0.699 0     0 0
   1.018 -1.018 1    0 0
   0     0     0     1 0
`;
const LIP_SATURATE_GENIE = "1";

const LIP_FIX_CONFIG = {
  Benjamin: { matrix: LIP_MATRIX_BENJAMIN, saturate: LIP_SATURATE_BENJAMIN },
  John:     { matrix: LIP_MATRIX_JOHN,     saturate: LIP_SATURATE_JOHN },
  Genie:    { matrix: LIP_MATRIX_GENIE,    saturate: LIP_SATURATE_GENIE },
};

function LipColorFix({ id, avatarName }) {
  const cfg = LIP_FIX_CONFIG[avatarName] || LIP_FIX_CONFIG.John;
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        <filter id={id} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values={cfg.matrix} />
          <feColorMatrix type="saturate" values={cfg.saturate} />
        </filter>
      </defs>
    </svg>
  );
}

export default function TwoDAvatar({ emotion = "neutral", avatarName = "Camille", theme }) {
  const [mouthFrame, setMouthFrame] = useState(0);
  const [imgSrc, setImgSrc]         = useState("");
  const [imgSize, setImgSize]       = useState({ w: 0, h: 0 });
  const [natural, setNatural]       = useState(null); // the loaded image's own pixel size
  const imgRef                      = useRef(null);
  const intervalRef                 = useRef(null);
  const seqRef                      = useRef(0);

  // FIX 5a: isTalkingRef tracks current animation state — reset explicitly in speak()
  const isTalkingRef    = useRef(false);
  const avatarNameRef   = useRef(avatarName);
  const safetyTimerRef  = useRef(null);

  useEffect(() => { avatarNameRef.current = avatarName; }, [avatarName]);

  const isMale   = avatarName === "Benjamin" || avatarName === "John";
  // Lip colour filter for every avatar that has one (Benjamin, John, Genie).
  const hasLipFix = Object.prototype.hasOwnProperty.call(LIP_FIX_CONFIG, avatarName);
  const lipFixId = `lip-color-fix-${avatarName}`;

  // Load emotion image
  useEffect(() => { preloadHost(avatarName); }, [avatarName]);
  useEffect(() => {
    const map = AVATAR_IMAGES[avatarName] || AVATAR_IMAGES.Camille;
    setImgSrc(map[emotion] || map.neutral);
  }, [avatarName, emotion]);

  const handleImgLoad = () => {
    if (imgRef.current) {
      setImgSize({ w: imgRef.current.offsetWidth, h: imgRef.current.offsetHeight });
      setNatural({ src: imgRef.current.getAttribute("src"), w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (imgRef.current) setImgSize({ w: imgRef.current.offsetWidth, h: imgRef.current.offsetHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── FIX 5b: startTalking / stopTalking — explicit state management ──────────
  const startTalking = () => {
    // FIX: Do NOT guard with isTalkingRef here — let it restart cleanly
    // The guard was causing Benjamin/John to silently skip animation
    isTalkingRef.current = true;
    window._ttsPlaying = true;
    seqRef.current = 0;
    clearInterval(intervalRef.current);
    clearTimeout(safetyTimerRef.current);
    intervalRef.current = setInterval(() => {
      if (!isTalkingRef.current) { clearInterval(intervalRef.current); return; }
      seqRef.current = (seqRef.current + 1) % TALK_SEQ.length;
      setMouthFrame(TALK_SEQ[seqRef.current]);
    }, TALK_INTERVAL);
    console.log(`[2D TTS] 👄 startTalking — ${avatarNameRef.current}`);
  };

  const stopTalking = () => {
    isTalkingRef.current = false;
    window._ttsPlaying = false;
    clearInterval(intervalRef.current);
    clearTimeout(safetyTimerRef.current);
    setMouthFrame(0);
    console.log(`[2D TTS] 🔇 stopTalking — ${avatarNameRef.current}`);
  };

  // ── Speak function — registered globally ─────────────────────────────────────
  useEffect(() => {
    const speak = (text) => {
      if (!text) return;

      // FIX 5c: ALWAYS reset isTalkingRef before starting — prevents ghost-block
      // This was the key bug: if a previous speak() left isTalkingRef=true
      // (e.g. due to avatar switch mid-speech), new calls were silently blocked
      isTalkingRef.current = false;
      clearInterval(intervalRef.current);
      clearTimeout(safetyTimerRef.current);

      // Cancel any current speech
      window.speechSynthesis?.cancel();

      // The avatar's own (3D) voice first; the browser voice only if that fails.
      speakWithAvatarVoice(text, avatarNameRef.current, { onStart: startTalking, onEnd: stopTalking })
        .then((spoke) => { if (!spoke) speakBrowser(text); });
    };

    const speakBrowser = (text) => {
      // Small delay after cancel (browser quirk — needed on Chrome)
      setTimeout(() => {
        if (!window.speechSynthesis) {
          // No speech API — animate mouth for estimated duration
          startTalking();
          const duration = Math.max(2000, text.split(" ").length * 350);
          safetyTimerRef.current = setTimeout(stopTalking, duration);
          return;
        }

        const utter = new SpeechSynthesisUtterance(text);
        const name  = avatarNameRef.current;
        const male  = name === "Benjamin" || name === "John";

        utter.pitch = male ? 0.85 : 1.15;
        utter.rate  = 1.0;
        utter.lang  = "en-US";

        // Assign voice — retry if voices not loaded yet
        const trySetVoice = () => {
          const v = getVoice(name);
          if (v) { utter.voice = v; return true; }
          return false;
        };
        if (!trySetVoice()) {
          window.speechSynthesis.onvoiceschanged = () => {
            trySetVoice();
            window.speechSynthesis.onvoiceschanged = null;
          };
        }

        // FIX 5d: Start mouth animation IMMEDIATELY before speak() call
        // Don't wait for onstart — it fires late or not at all on some browsers
        // for male voices (this was the Benjamin/John specific bug)
        startTalking();

        utter.onstart = () => {
          // Belt + suspenders: also restart on onstart in case of delay
          console.log(`[2D TTS] onstart — ${name}`);
          if (!isTalkingRef.current) startTalking();
        };
        utter.onend = () => {
          console.log(`[2D TTS] onend — ${name}`);
          stopTalking();
        };
        utter.onerror = (e) => {
          // FIX: 'interrupted' error is normal on cancel — don't log as warning
          if (e.error !== 'interrupted') {
            console.warn("[2D TTS] error:", e.error, "| avatar:", name);
          }
          stopTalking();
        };

        window.speechSynthesis.speak(utter);

        // FIX 5e: Safety fallback — if onend never fires (browser bug),
        // stop animation after estimated duration + buffer
        const wordCount = text.split(" ").length;
        const maxDuration = Math.max(5000, wordCount * 600);
        safetyTimerRef.current = setTimeout(() => {
          if (isTalkingRef.current) {
            console.warn("[2D TTS] Safety timeout fired — force stopping mouth");
            stopTalking();
          }
        }, maxDuration);

      }, 120); // 120ms after cancel — slightly longer than before for reliability
    };

    window.camille2DSpeak = speak;
    window.camille2DStop  = () => {
      stopAvatarVoice();
      window.speechSynthesis?.cancel();
      stopTalking();
    };

    // Cleanup on unmount
    return () => {
      window.camille2DSpeak = null;
      window.camille2DStop  = null;
      stopAvatarVoice();
      clearInterval(intervalRef.current);
      clearTimeout(safetyTimerRef.current);
    };
  }, []); // Empty deps — uses refs, no stale closures

  // Mouth pixel position
  const cfgNow      = AVATAR_CONFIG[avatarName] || AVATAR_CONFIG.Camille;
  const mouthTopPx  = imgSize.h > 0 ? (imgSize.h * cfgNow.mouthTop  / 100) + cfgNow.offsetY : 0;
  const mouthLeftPx = imgSize.w > 0 ? (imgSize.w * cfgNow.mouthLeft / 100) + cfgNow.offsetX : 0;

  // Image-relative mouth (avatars with `mouth`, i.e. Margie): percentages of the
  // image's own natural size, so image and mouth stay locked together at any
  // viewport. Uses the loaded image's natural size (an image with a different
  // size gets its own percentages); the config's frame size until it loads.
  const relMouth = cfgNow.mouth || null;
  const loaded = natural && natural.src === imgSrc ? natural : null;
  const natW = (loaded && loaded.w) || (relMouth && relMouth.natW) || 0;
  const natH = (loaded && loaded.h) || (relMouth && relMouth.natH) || 0;

  return (
    <div style={{
      position: "relative", width: "100%", height: "100%",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      paddingTop: `${8 + (cfgNow.imgTop || 0)}px`,
      marginLeft: `${cfgNow.imgLeft || 0}px`,
      background: "transparent",
    }}>
      {/* Lip color fix SVG filter def — only rendered for male avatars */}
      {hasLipFix && <LipColorFix id={lipFixId} avatarName={avatarName} />}

      {/* One wrapper for the image and the mouth: it takes the image's box (and,
          for an image-relative mouth, the image's own aspect ratio), so the
          mouth's percentages are percentages of the rendered image. */}
      <div style={{
        position: "relative", display: "inline-block",
        ...(relMouth && natW && natH ? { aspectRatio: `${natW} / ${natH}`, lineHeight: 0 } : {}),
      }}>

        {/* Avatar image — CSS filter applied HERE on the avatar, NOT the mouth */}
        <img
          ref={imgRef}
          src={imgSrc}
          alt={avatarName}
          onLoad={handleImgLoad}
          style={{
            display: "block",
            maxWidth: `min(${cfgNow.imgWidth || "300px"}, 80vw)`,
            maxHeight: `min(${cfgNow.imgHeight || "400px"}, 65vh)`,
            width: "auto", height: "auto",
            userSelect: "none", pointerEvents: "none",
            // Male avatar image color correction — separate from mouth overlay,
            // which is a distinct layered PNG handled below.
            filter: isMale
              ? avatarName === "Benjamin"
                ? "sepia(0.3) saturate(0.8) brightness(0.9) hue-rotate(5deg)"
                : "sepia(0.1) saturate(0.95) brightness(0.92)"
              : "none",
          }}
          draggable={false}
        />

        {/* FIX 5f / Sprint 4B: Mouth overlay lip-color fix for Benjamin & John.
            The shared MOUTHS PNGs are pink/red-toned, reading as feminine on
            male avatars. SVG color-matrix filter (defined above as LipColorFix)
            is applied directly to this mouth image — pulls red/pink channel
            relationship toward neutral so lips don't read as lipstick-pink.
            STOPGAP ONLY — replace with BizBarrett's original mood-card mouth
            assets when supplied; this is a tone correction, not new artwork. */}
        {relMouth && natW > 0 && natH > 0 && (
          <img
            src={mouthFrame === 0 && REST_MOUTH[avatarName]?.[emotion] ? REST_MOUTH[avatarName][emotion] : MOUTHS[mouthFrame]}
            alt=""
            style={{
              position: "absolute",
              left: `${(relMouth.x / natW) * 100}%`,
              top: `${(relMouth.y / natH) * 100}%`,
              width: `${(relMouth.w / natW) * 100}%`,
              height: "auto",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              transition: "all 0.05s ease",
              userSelect: "none",
              pointerEvents: "none",
            }}
            draggable={false}
          />
        )}
        {!relMouth && imgSize.h > 0 && (
          <div style={{
            position: "absolute",
            top: `${mouthTopPx}px`,
            left: `${mouthLeftPx}px`,
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            pointerEvents: "none",
          }}>
            <img
              src={mouthFrame === 0 && REST_MOUTH[avatarName]?.[emotion] ? REST_MOUTH[avatarName][emotion] : MOUTHS[mouthFrame]}
              alt=""
              style={{
                width: `${cfgNow.mouthSize}px`,
                height: "auto",
                transition: "all 0.05s ease",
                userSelect: "none",
                pointerEvents: "none",
                filter: hasLipFix ? `url(#${lipFixId})` : "none",
              }}
              draggable={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
